import playingWaveAnimation from '@/assets/animation/playing-wave.json';
import { FilterPlayIcon } from '@/components/Icons';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FilterGroup } from '@/service/type/Filter';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { cn, getInitials } from '@/utils';
import Lottie from 'lottie-react';
import React, { useRef, useEffect, useState } from 'react';
import { getFilterBalance } from '../../utils/getFilterBalance';
import { useStyleFilter } from '../../hooks/useStyleFilter';
import useFlags from '@jenmusic/feature-flag';
import { useNavigate } from 'react-router-dom';

interface StyleFilterListProps {
  defaultCurrentFilter?: FilterGroup;
  onSelect: (val: FilterGroup) => void;
  selectedSfs: FilterGroup[];
}

const StyleFilterList: React.FC<StyleFilterListProps> = ({ onSelect, selectedSfs }) => {
  const { showShopFilters } = useFlags();
  const { postStyleFilterOrderRun } = useStyleFilter();
  const containerRef = useRef<HTMLDivElement>(null);
  const { filterGroupList } = useGeneratorFilterStore();

  const allSfList = filterGroupList ?? [];
  const isEmpty = !allSfList?.length;

  return (
    <div className={cn('w-full h-full bg-[#131415] text-white px-2 ')}>
      <div ref={containerRef} className="hidden" />
      <ScrollArea className="space-y-4 h-full overflow-auto px-2 m-0" thumbClassName="bg-[#414243] hover:bg-[#414243]/80">
        {allSfList.map((filter) => {
          return (
            <StyleFilterListItem
              isSelected={Boolean(selectedSfs.find((sf) => sf.id === filter.id))}
              onSelect={onSelect}
              postStyleFilterOrderRun={postStyleFilterOrderRun}
              filter={filter}
              key={filter.id}
            />
          );
        })}
        {showShopFilters && <StyleFilterMore className={isEmpty ? 'mt-[150px]' : ''} />}
      </ScrollArea>
    </div>
  );
};

const StyleFilterListItem = ({
  filter,
  postStyleFilterOrderRun,
  onSelect,
  isSelected,
}: {
  filter: FilterGroup;
  postStyleFilterOrderRun: any;
  onSelect: (val: FilterGroup) => void;
  isSelected: boolean;
}) => {
  const { currentStrength, setCurrentStrength } = useGeneratorFilterStore();
  const { play, playAudioInfo, isPlaying, pause } = useAudioPlayerStore();

  const balanceData = getFilterBalance({
    filter: filter,
    currentStrength: currentStrength,
    isSelected: isSelected,
  });

  const balance = balanceData.balance;
  const hasWarning = balanceData.hasWarning;

  const isCurrentPlaying = isPlaying && playAudioInfo?.id === String(filter.id);

  const handlePlay = (filter: FilterGroup) => {
    if (isCurrentPlaying) {
      pause();
    } else {
      play({
        url: filter.audio,
        name: filter.name,
        id: String(filter.id),
      });
    }
  };

  return (
    <div
      onClick={() => {
        onSelect(filter);
      }}
      className={cn('flex items-center gap-2 group cursor-pointer hover:bg-[#1c1c1d] rounded p-2', isSelected && 'text-jen-orange')}
    >
      <div
        className={cn('w-4 h-4 flex items-center justify-center', isSelected ? 'text-jen-orange' : 'text-white/60')}
        onClick={(e) => {
          e.stopPropagation();
          handlePlay(filter);
        }}
      >
        {isCurrentPlaying ? <CircularProgressWithPause /> : <FilterPlayIcon />}
      </div>

      <div className="relative shrink-0">
        {(filter.thumb_album_cover ?? filter.album_cover) ? (
          <img
            src={filter.thumb_album_cover ?? filter.album_cover}
            className={cn('w-[40px] h-[60px] block rounded object-cover bg-cover flex-shrink-0 ')}
          />
        ) : (
          <div className={cn('w-[40px] h-[60px] block rounded bg-cover flex-shrink-0 bg-jen-orange text-center ')}>
            {filter?.artist && (
              <div className="absolute bottom-[14px] w-full">
                <span className="text-[22px] text-white uppercase">{getInitials(filter.artist)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={cn('flex-grow')}>
        <div
          className={cn('text-[0.75rem] text-[#9D9DA6] font-pressura-mono uppercase', isSelected ? 'text-jen-orange' : 'text-[#9d9da6]')}
        >
          {filter.artist}
        </div>
        <div
          className={cn('text-[0.75rem] text-[#9D9DA6] font-pressura-mono uppercase', isSelected ? 'text-jen-orange' : 'text-[#9d9da6]')}
        >
          {filter.name}
        </div>
      </div>
      {isCurrentPlaying ? (
        <Lottie className="w-[16px] shrink-0" animationData={playingWaveAnimation} loop={true} />
      ) : (
        <div className="w-[16px] shrink-0" />
      )}

      <div
        className={cn(
          'flex items-center shrink-0 text-[0.75rem] font-pressura-mono uppercase',
          isSelected ? 'text-jen-orange' : 'text-[#9d9da6]',
          {
            'text-jen-active-primary': hasWarning,
          },
        )}
      >
        {hasWarning && <InfoIcon />}
        {balance >= 0 ? `${balance} mins` : 'unlimited mins'}
      </div>
    </div>
  );
};

const StyleFilterMore = ({ className }: { className?: string }) => {
  const navigate = useNavigate();
  return (
    <div className={cn('flex flex-col text-[#efeff0] text-center my-4 w-full', className)}>
      <div className="text-[17px] text-[#EFEFF0]">Add More Filters</div>
      <div className="tracking-[-0.32px] w-[206px] mx-auto text-[14px] text-[#999A9B]">
        Shop StyleFilters to apply unique musical signatures to your track.
      </div>
      <div
        className="cursor-pointer hover:opacity-90 mt-[12px] text-[12px] p-2 bg-[#FF5B27] rounded w-fit mx-auto"
        onClick={() => {
          navigate('/marketplace');
        }}
      >
        Shop StyleFilters<sup className="top-[-8px] text-[6px]">TM</sup>
      </div>
    </div>
  );
};

export const InfoIcon = () => {
  return (
    <svg className="inline-block mr-1" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M7.99967 14.6668C11.6816 14.6668 14.6663 11.6821 14.6663 8.00016C14.6663 4.31826 11.6816 1.3335 7.99967 1.3335C4.31778 1.3335 1.33301 4.31826 1.33301 8.00016C1.33301 11.6821 4.31778 14.6668 7.99967 14.6668Z"
        fill="#FF5B27"
      />
      <path d="M8 5.3335V8.00016" stroke="#111112" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M8 10.6665H8.00667" stroke="#111112" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
};

interface CircularProgressWithPauseProps {}

export const CircularProgressWithPause: React.FC<CircularProgressWithPauseProps> = () => {
  const { currentTime, duration } = useAudioPlayerStore();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (duration && duration > 0) {
      setProgress((currentTime / duration) * 100);
    } else {
      setProgress(0);
    }
  }, [currentTime, duration]);

  const size = 16;
  const strokeWidth = 1;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255, 91, 39, 0.3)" strokeWidth={strokeWidth} />

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#FF5B27"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (progress / 100) * circumference}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center w-full h-full">
        <div className="flex  justify-center gap-[2px]">
          <div className="w-[2px] h-[5px] bg-jenmusic-interactive rounded-[1px]"></div>
          <div className="w-[2px] h-[5px] bg-jenmusic-interactive rounded-[1px]"></div>
        </div>
      </div>
    </div>
  );
};

export const SfLowIcon = (props: any) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="35" height="21" viewBox="0 0 35 21" fill="none" {...props}>
      <rect width="34.157" height="17.0785" rx="8.53924" fill="#1D1E1F" />
      <rect
        x="0.197362"
        y="0.197362"
        width="33.7622"
        height="16.6838"
        rx="8.34188"
        stroke="url(#paint0_linear_3896_91338)"
        stroke-opacity="0.1"
        stroke-width="0.394724"
      />
      <g filter="url(#filter0_iiii_3896_91338)">
        <rect
          x="2.84766"
          y="14.9438"
          width="12.8089"
          height="28.4641"
          rx="6.40443"
          transform="rotate(-90 2.84766 14.9438)"
          fill="#161718"
        />
      </g>
      <rect
        x="3.02556"
        y="14.7659"
        width="12.4531"
        height="28.1083"
        rx="6.22653"
        transform="rotate(-90 3.02556 14.7659)"
        stroke="url(#paint1_linear_3896_91338)"
        stroke-width="0.355802"
      />
      <g filter="url(#filter1_iiii_3896_91338)">
        <rect
          x="4.625"
          y="13.1655"
          width="9.25084"
          height="24.9061"
          rx="4.62542"
          transform="rotate(-90 4.625 13.1655)"
          fill="url(#paint2_linear_3896_91338)"
        />
      </g>
      <rect
        width="8.53924"
        height="24.1945"
        rx="4.26962"
        transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 29.1748 12.8091)"
        fill="url(#paint3_radial_3896_91338)"
      />
      <g filter="url(#filter2_ddddd_3896_91338)">
        <rect
          x="4.27246"
          y="13.876"
          width="10.6741"
          height="17.7901"
          rx="5.33703"
          transform="rotate(-90 4.27246 13.876)"
          fill="url(#paint4_linear_3896_91338)"
        />
        <rect
          x="4.45036"
          y="13.6981"
          width="10.3183"
          height="17.4343"
          rx="5.15913"
          transform="rotate(-90 4.45036 13.6981)"
          stroke="url(#paint5_linear_3896_91338)"
          stroke-width="0.355802"
        />
      </g>
      <ellipse cx="8.89917" cy="8.53883" rx="3.91382" ry="4.44752" fill="url(#paint6_linear_3896_91338)" />
      <ellipse cx="17.4382" cy="8.53883" rx="3.91382" ry="4.44752" fill="url(#paint7_linear_3896_91338)" />
      <defs>
        <filter
          id="filter0_iiii_3896_91338"
          x="2.84766"
          y="2.13477"
          width="30.9545"
          height="15.2997"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.355802" dy="0.355802" />
          <feGaussianBlur stdDeviation="0.355802" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1.06741" dy="1.06741" />
          <feGaussianBlur stdDeviation="0.711603" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.09 0" />
          <feBlend mode="normal" in2="effect1_innerShadow_3896_91338" result="effect2_innerShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="2.49061" dy="2.49061" />
          <feGaussianBlur stdDeviation="1.06741" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_3896_91338" result="effect3_innerShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="4.26962" dy="4.62542" />
          <feGaussianBlur stdDeviation="1.24531" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.01 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_3896_91338" result="effect4_innerShadow_3896_91338" />
        </filter>
        <filter
          id="filter1_iiii_3896_91338"
          x="4.625"
          y="3.91455"
          width="26.6853"
          height="11.03"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="0.355802" />
          <feGaussianBlur stdDeviation="0.355802" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.711603" dy="0.711603" />
          <feGaussianBlur stdDeviation="0.533703" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.21 0" />
          <feBlend mode="normal" in2="effect1_innerShadow_3896_91338" result="effect2_innerShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1.42321" dy="1.77901" />
          <feGaussianBlur stdDeviation="0.711603" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.13 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_3896_91338" result="effect3_innerShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="2.49061" dy="3.20222" />
          <feGaussianBlur stdDeviation="0.889504" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_3896_91338" result="effect4_innerShadow_3896_91338" />
        </filter>
        <filter
          id="filter2_ddddd_3896_91338"
          x="3.56086"
          y="2.84635"
          width="24.5503"
          height="17.4341"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="0.355802" />
          <feGaussianBlur stdDeviation="0.355802" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.29 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.711603" dy="0.711603" />
          <feGaussianBlur stdDeviation="0.533703" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.26 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_3896_91338" result="effect2_dropShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1.42321" dy="1.77901" />
          <feGaussianBlur stdDeviation="0.711603" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
          <feBlend mode="normal" in2="effect2_dropShadow_3896_91338" result="effect3_dropShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="2.84641" dy="2.84641" />
          <feGaussianBlur stdDeviation="0.889504" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
          <feBlend mode="normal" in2="effect3_dropShadow_3896_91338" result="effect4_dropShadow_3896_91338" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="4.26962" dy="4.62542" />
          <feGaussianBlur stdDeviation="0.889504" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.01 0" />
          <feBlend mode="normal" in2="effect4_dropShadow_3896_91338" result="effect5_dropShadow_3896_91338" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect5_dropShadow_3896_91338" result="shape" />
        </filter>
        <linearGradient id="paint0_linear_3896_91338" x1="17.0785" y1="0" x2="17.0785" y2="18.8924" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" />
        </linearGradient>
        <linearGradient id="paint1_linear_3896_91338" x1="9.25209" y1="14.9438" x2="2.26851" y2="35.5672" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" stop-opacity="0.15" />
        </linearGradient>
        <linearGradient id="paint2_linear_3896_91338" x1="9.25042" y1="13.1655" x2="9.25042" y2="38.0716" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FF5B27" />
          <stop offset="1" stop-color="#993717" />
        </linearGradient>
        <radialGradient
          id="paint3_radial_3896_91338"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(4.26962 20.2807) rotate(-90) scale(8.18344 3.47615)"
        >
          <stop stop-color="#FF8227" stop-opacity="0.7" />
          <stop offset="1" stop-color="#FF8227" stop-opacity="0" />
        </radialGradient>
        <linearGradient id="paint4_linear_3896_91338" x1="21.4399" y1="29.7092" x2="15.0019" y2="39.7778" gradientUnits="userSpaceOnUse">
          <stop stop-color="#2B2D2F" />
          <stop offset="1" stop-color="#1B1D1E" />
        </linearGradient>
        <linearGradient id="paint5_linear_3896_91338" x1="6.94097" y1="24.7279" x2="9.60949" y2="13.876" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" stop-opacity="0.15" />
        </linearGradient>
        <linearGradient id="paint6_linear_3896_91338" x1="4.98535" y1="8.37411" x2="12.813" y2="8.37411" gradientUnits="userSpaceOnUse">
          <stop stop-color="#202223" />
          <stop offset="1" stop-color="#3C3E40" />
        </linearGradient>
        <linearGradient id="paint7_linear_3896_91338" x1="13.5244" y1="8.37411" x2="21.3521" y2="8.37411" gradientUnits="userSpaceOnUse">
          <stop stop-color="#1D1E1F" />
          <stop offset="1" stop-color="#3E4040" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export const SfHighIcon = (props: any) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="37" height="21" viewBox="0 0 37 21" fill="none" {...props}>
      <rect width="34.157" height="17.0785" rx="8.53924" fill="#1D1E1F" />
      <rect
        x="0.197362"
        y="0.197362"
        width="33.7622"
        height="16.6838"
        rx="8.34188"
        stroke="url(#paint0_linear_3896_91353)"
        stroke-opacity="0.1"
        stroke-width="0.394724"
      />
      <g filter="url(#filter0_iiii_3896_91353)">
        <rect
          x="2.84668"
          y="14.9438"
          width="12.8089"
          height="28.4641"
          rx="6.40443"
          transform="rotate(-90 2.84668 14.9438)"
          fill="#161718"
        />
      </g>
      <rect
        x="3.02458"
        y="14.7659"
        width="12.4531"
        height="28.1083"
        rx="6.22653"
        transform="rotate(-90 3.02458 14.7659)"
        stroke="url(#paint1_linear_3896_91353)"
        stroke-width="0.355802"
      />
      <g filter="url(#filter1_iiii_3896_91353)">
        <rect
          x="4.62598"
          y="13.1655"
          width="9.25084"
          height="24.9061"
          rx="4.62542"
          transform="rotate(-90 4.62598 13.1655)"
          fill="url(#paint2_linear_3896_91353)"
        />
      </g>
      <g filter="url(#filter2_ddddd_3896_91353)">
        <rect
          x="12.4531"
          y="13.8765"
          width="10.6741"
          height="17.7901"
          rx="5.33703"
          transform="rotate(-90 12.4531 13.8765)"
          fill="url(#paint3_linear_3896_91353)"
        />
        <rect
          x="12.631"
          y="13.6986"
          width="10.3183"
          height="17.4343"
          rx="5.15913"
          transform="rotate(-90 12.631 13.6986)"
          stroke="url(#paint4_linear_3896_91353)"
          stroke-width="0.355802"
        />
      </g>
      <ellipse cx="17.0798" cy="8.53981" rx="3.91382" ry="4.44752" fill="url(#paint5_linear_3896_91353)" />
      <ellipse cx="25.6189" cy="8.53981" rx="3.91382" ry="4.44752" fill="url(#paint6_linear_3896_91353)" />
      <defs>
        <filter
          id="filter0_iiii_3896_91353"
          x="2.84668"
          y="2.13477"
          width="30.9545"
          height="15.2997"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.355802" dy="0.355802" />
          <feGaussianBlur stdDeviation="0.355802" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1.06741" dy="1.06741" />
          <feGaussianBlur stdDeviation="0.711603" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.09 0" />
          <feBlend mode="normal" in2="effect1_innerShadow_3896_91353" result="effect2_innerShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="2.49061" dy="2.49061" />
          <feGaussianBlur stdDeviation="1.06741" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_3896_91353" result="effect3_innerShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="4.26962" dy="4.62542" />
          <feGaussianBlur stdDeviation="1.24531" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.01 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_3896_91353" result="effect4_innerShadow_3896_91353" />
        </filter>
        <filter
          id="filter1_iiii_3896_91353"
          x="4.62598"
          y="3.91455"
          width="26.6853"
          height="11.03"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="0.355802" />
          <feGaussianBlur stdDeviation="0.355802" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.711603" dy="0.711603" />
          <feGaussianBlur stdDeviation="0.533703" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.21 0" />
          <feBlend mode="normal" in2="effect1_innerShadow_3896_91353" result="effect2_innerShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1.42321" dy="1.77901" />
          <feGaussianBlur stdDeviation="0.711603" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.13 0" />
          <feBlend mode="normal" in2="effect2_innerShadow_3896_91353" result="effect3_innerShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="2.49061" dy="3.20222" />
          <feGaussianBlur stdDeviation="0.889504" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
          <feBlend mode="normal" in2="effect3_innerShadow_3896_91353" result="effect4_innerShadow_3896_91353" />
        </filter>
        <filter
          id="filter2_ddddd_3896_91353"
          x="11.7415"
          y="2.84683"
          width="24.5503"
          height="17.4341"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="0.355802" />
          <feGaussianBlur stdDeviation="0.355802" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.29 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="0.711603" dy="0.711603" />
          <feGaussianBlur stdDeviation="0.533703" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.26 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_3896_91353" result="effect2_dropShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1.42321" dy="1.77901" />
          <feGaussianBlur stdDeviation="0.711603" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
          <feBlend mode="normal" in2="effect2_dropShadow_3896_91353" result="effect3_dropShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="2.84641" dy="2.84641" />
          <feGaussianBlur stdDeviation="0.889504" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0" />
          <feBlend mode="normal" in2="effect3_dropShadow_3896_91353" result="effect4_dropShadow_3896_91353" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="4.26962" dy="4.62542" />
          <feGaussianBlur stdDeviation="0.889504" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.01 0" />
          <feBlend mode="normal" in2="effect4_dropShadow_3896_91353" result="effect5_dropShadow_3896_91353" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect5_dropShadow_3896_91353" result="shape" />
        </filter>
        <linearGradient id="paint0_linear_3896_91353" x1="17.0785" y1="0" x2="17.0785" y2="18.8924" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" />
        </linearGradient>
        <linearGradient id="paint1_linear_3896_91353" x1="9.25111" y1="14.9438" x2="2.26754" y2="35.5672" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" stop-opacity="0.15" />
        </linearGradient>
        <linearGradient id="paint2_linear_3896_91353" x1="9.2514" y1="13.1655" x2="9.2514" y2="38.0716" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FF5B27" />
          <stop offset="1" stop-color="#993717" />
        </linearGradient>
        <linearGradient id="paint3_linear_3896_91353" x1="29.6206" y1="29.7096" x2="23.1825" y2="39.7783" gradientUnits="userSpaceOnUse">
          <stop stop-color="#2B2D2F" />
          <stop offset="1" stop-color="#1B1D1E" />
        </linearGradient>
        <linearGradient id="paint4_linear_3896_91353" x1="15.1216" y1="24.7284" x2="17.7902" y2="13.8765" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" stop-opacity="0.15" />
        </linearGradient>
        <linearGradient id="paint5_linear_3896_91353" x1="13.166" y1="8.37508" x2="20.9937" y2="8.37508" gradientUnits="userSpaceOnUse">
          <stop stop-color="#202223" />
          <stop offset="1" stop-color="#3C3E40" />
        </linearGradient>
        <linearGradient id="paint6_linear_3896_91353" x1="21.7051" y1="8.37508" x2="29.5327" y2="8.37508" gradientUnits="userSpaceOnUse">
          <stop stop-color="#1D1E1F" />
          <stop offset="1" stop-color="#3E4040" />
        </linearGradient>
      </defs>
    </svg>
  );
};

export default StyleFilterList;
